import types from '../../../mutation-types';
import MessageApi from '../../../../api/inbox/message';
import ConversationApi from '../../../../api/inbox/conversation';

const actions = {
  pinMessage: async ({ commit, state }, { conversationId, messageId }) => {
    try {
      await MessageApi.pin(conversationId, messageId);
      commit(types.PIN_MESSAGE, { conversationId, messageId });

      // Update the pinned messages cache
      // Try to get message from local store first
      const chat = state.allConversations.find(c => c.id === conversationId);
      let message = chat?.messages?.find(m => m.id === messageId);

      // If not in local messages, check cache (for old messages fetched via API)
      if (!message) {
        const cache = state.pinnedMessagesCache[conversationId] || [];
        message = cache.find(m => m.id === messageId);
      }

      if (message) {
        commit(types.UPDATE_PINNED_MESSAGE_IN_CACHE, {
          conversationId,
          message: {
            ...message,
            pinned: true,
            pinned_at: new Date().toISOString(),
          },
        });
      }
    } catch (error) {
      throw new Error(error);
    }
  },

  unpinMessage: async ({ commit }, { conversationId, messageId }) => {
    try {
      await MessageApi.unpin(conversationId, messageId);
      commit(types.UNPIN_MESSAGE, { conversationId, messageId });
      commit(types.REMOVE_PINNED_MESSAGE_FROM_CACHE, {
        conversationId,
        messageId,
      });
    } catch (error) {
      throw new Error(error);
    }
  },

  unpinAllMessages: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unpinAllMessages(conversationId);
      commit(types.UNPIN_ALL_MESSAGES, { conversationId });
      commit(types.CLEAR_PINNED_MESSAGES_CACHE, { conversationId });
    } catch (error) {
      throw new Error(error);
    }
  },

  fetchPinnedMessages: async (
    { commit },
    { conversationId, sortOrder = 'asc' }
  ) => {
    try {
      const { data: { payload } = {} } =
        await ConversationApi.getPinnedMessages(conversationId, sortOrder);
      commit(types.SET_PINNED_MESSAGES_CACHE, {
        conversationId,
        messages: payload || [],
      });
    } catch (error) {
      // Silently fail - pinned messages are not critical
      console.error('Failed to fetch pinned messages:', error);
    }
  },

  fetchMessagesAroundId: async (
    { commit, state },
    { conversationId, messageId }
  ) => {
    try {
      const { data: { payload } = {} } = await MessageApi.getMessagesAround({
        conversationId,
        messageId,
      });

      if (payload && payload.length > 0) {
        // Find the conversation
        const chat = state.allConversations.find(c => c.id === conversationId);
        if (!chat) return payload;

        // Filter out messages that already exist in the conversation
        const existingIds = new Set((chat.messages || []).map(m => m.id));
        const newMessages = payload.filter(m => !existingIds.has(m.id));

        if (newMessages.length > 0) {
          // Merge new messages with existing ones and sort by created_at
          const allMessages = [...(chat.messages || []), ...newMessages];
          allMessages.sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );

          // Use SET_MISSING_MESSAGES to update without triggering scroll events
          commit(types.SET_MISSING_MESSAGES, {
            id: conversationId,
            data: allMessages,
          });
        }
      }

      return payload;
    } catch (error) {
      console.error('Failed to fetch messages around ID:', error);
      throw error;
    }
  },
};

export default actions;
